import React, { useState, useEffect, useRef } from 'react';
import { Card, Empty, Tag, Button, Space, Tooltip, Popconfirm, message, Modal, Form, Input, Select, Switch, InputNumber, Avatar } from 'antd';
import { 
  CalendarOutlined, 
  UserOutlined, 
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BellOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import { dealsApi, show422Error } from '../../../utils/api';
import { resolveAvatarSrc } from '../../../utils/avatarUtils';
import { scrollToFirstError } from '../../../utils/formHelpers';
import FollowUpActivityPicker from '../../common/FollowUpActivityPicker';
import { ActivityTypeBadge } from '../../common/FollowUpActivitySelect';
import { normalizeFollowUpActivities, mergeFollowUpActivities, resolveFollowUpActivity, isNoAnswerFollowUpActivity, NO_ANSWER_FOLLOW_UP_REMARK } from '../../../utils/followUpActivityUtils';
import FollowUpSchedulePicker from '../../common/FollowUpSchedulePicker';
import {
  formatFollowUpDateTime,
  isFollowUpEdited,
  formatFollowUpDisplayLabel,
  isDoNowFollowUp,
  isImmediateFollowUp,
  resolveFollowUpStatus,
  resolveFollowUpCompletedAt,
  getFollowUpStatusLabel,
  FOLLOW_UP_STATUS_OPTIONS,
  getFollowUpStatusTagColor,
  normalizeFollowUpStatusForForm,
  COMPLETED_FOLLOWUP_DATE_CLASS,
  COMPLETED_FOLLOWUP_TEXT_CLASS,
} from '../../../utils/followUpUtils';
import ImmediateFollowUpBadge from '../../common/ImmediateFollowUpBadge';
import { useAuth } from '../../../contexts/AuthContext';
import { isPermissionAllowed } from '../../../utils/permissions';
import { canManageLeadRecord, canDeleteLeadRecord } from '../../../utils/leadRecordPermissions';

const { TextArea } = Input;
const { Option } = Select;

const resolvePersonName = (person) => {
  if (!person) return null;
  if (typeof person === 'object') return person.name || null;
  return null;
};

const resolvePersonId = (person) => {
  if (person == null) return null;
  if (typeof person === 'object') return person.id ?? null;
  return person;
};

const resolvePersonAvatar = (person, employees = []) => {
  if (person && typeof person === 'object') {
    return person.image_url || person.image || null;
  }
  const id = resolvePersonId(person);
  if (id == null) return null;
  const employee = employees.find((e) => e.id === id);
  return employee?.image_url || employee?.image || null;
};

// نرمال‌سازی داده‌های پیگیری - پشتیبانی از هر دو فرمت آرایه و آبجکت
const normalizeFollowUps = (data) => {
  if (Array.isArray(data)) return data;
  if (data?.follow_ups) return data.follow_ups;
  return [];
};

const FollowUpTab = ({ dealId, data = [], filters, permissions, onRefresh }) => {
  const { user } = useAuth();
  const [followUps, setFollowUps] = useState(normalizeFollowUps(data));
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [modalDataLoading, setModalDataLoading] = useState(false);
  const [doNow, setDoNow] = useState(false);
  const [sendReminder, setSendReminder] = useState(false);
  const [editingRemarkId, setEditingRemarkId] = useState(null);
  const [quickRemarkValue, setQuickRemarkValue] = useState('');
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const createDataLoadedRef = useRef(false);
  const activitiesCatalogRef = useRef([]);
  const submitSaveModeRef = useRef('close');

  useEffect(() => {
    createDataLoadedRef.current = false;
    activitiesCatalogRef.current = [];
  }, [dealId]);

  useEffect(() => {
    const items = normalizeFollowUps(data);
    setFollowUps(items);
  }, [data]);

  useEffect(() => {
    const items = normalizeFollowUps(data);
    const needsLookup = modalVisible || (items && items.length > 0);
    if (!needsLookup || createDataLoadedRef.current) return;
    createDataLoadedRef.current = true;
    fetchCreateData();
  }, [modalVisible, data]);

  const fetchCreateData = async () => {
    setEmployeesLoading(true);
    setActivitiesLoading(true);
    setModalDataLoading(true);
    try {
      const res = await dealsApi.getFollowUpCreateData();
      const data = res.data || {};
      
      // دریافت activities
      const activitiesList = normalizeFollowUpActivities(data.activities || []);
      activitiesCatalogRef.current = activitiesList;
      setActivities(activitiesList);
      
      // دریافت colleagues
      const colleaguesList = (data.colleagues || []).map((item) => ({
        id: item.id,
        name: item.name || '--',
        image_url: item.image_url || null,
      }));
      setEmployees(colleaguesList);
    } catch (error) {
      console.error('Error fetching create data:', error);
      message.error('خطا در دریافت اطلاعات');
    } finally {
      setEmployeesLoading(false);
      setActivitiesLoading(false);
      setModalDataLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = moment(dateString);
      return date.format('jYYYY/jMM/jDD HH:mm');
    } catch (e) {
      return null;
    }
  };

  const isOverdue = (dateString) => {
    if (!dateString) return false;
    try {
      const date = moment(dateString);
      return date.isBefore(moment(), 'day');
    } catch (e) {
      return false;
    }
  };

  const isToday = (dateString) => {
    if (!dateString) return false;
    try {
      const date = moment(dateString);
      return date.isSame(moment(), 'day');
    } catch (e) {
      return false;
    }
  };

  const handleOpenModal = (followUp = null) => {
    setEditingFollowUp(followUp);
    if (followUp) {
      setEditLoading(true);
      setModalVisible(true);
      dealsApi.getFollowUp(followUp.id)
        .then((res) => {
          const followUpData = res.follow_up || res.data?.follow_up || {};
          const isDoNow = followUpData.do_now === true || followUpData.do_now === 1 || followUpData.do_now === '1';
          setDoNow(isDoNow);
          const isSendReminder = followUpData.send_reminder === true || followUpData.send_reminder === 'true' || followUpData.send_reminder === 1 || followUpData.send_reminder === '1' || followUpData.send_reminder === 'yes';
          setSendReminder(isSendReminder);
          
          let nextFollowUpDate = null;
          if (followUpData.next_follow_up_date) {
            try {
              const date = moment(followUpData.next_follow_up_date);
              nextFollowUpDate = date.format('jYYYY/jMM/jDD HH:mm');
            } catch (e) {
              console.error('Error parsing date:', e);
              nextFollowUpDate = null;
            }
          }

          form.setFieldsValue({
            do_now: isDoNow,
            follow_up_activity_id: followUpData.follow_up_activity_id,
            next_follow_up_date: nextFollowUpDate,
            duration: followUpData.duration ? parseInt(followUpData.duration) : null,
            assign_to: resolvePersonId(followUpData.assign_to),
            send_reminder: isSendReminder,
            remind_time: followUpData.remind_time ? parseInt(followUpData.remind_time) : null,
            remind_type: followUpData.remind_type || 'minute',
            remark: followUpData.remark || '',
            status: normalizeFollowUpStatusForForm(followUpData.status ?? followUp.status),
          });
          
          if (res.activities) {
            setActivities((prev) => {
              const base = activitiesCatalogRef.current.length ? activitiesCatalogRef.current : prev;
              if (!base.length) return prev;
              return mergeFollowUpActivities(base, res.activities);
            });
          }
          if (res.colleagues) {
            setEmployees(res.colleagues.map(item => ({
              id: item.id,
              name: item.name,
              image_url: item.image_url || null,
            })));
          }
        })
        .catch((error) => {
          console.error('Error fetching follow-up:', error);
          message.error('خطا در دریافت اطلاعات پیگیری');
          setModalVisible(false);
        })
        .finally(() => {
          setEditLoading(false);
        });
    } else {
      setDoNow(false);
      setSendReminder(false);
      form.resetFields();
      form.setFieldsValue({
        do_now: false,
        send_reminder: false,
        remind_type: 'minute',
        assign_to: user?.id ?? undefined,
      });
      setModalVisible(true);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingFollowUp(null);
    form.resetFields();
    setDoNow(false);
    setSendReminder(false);
    if (activitiesCatalogRef.current.length) {
      setActivities(activitiesCatalogRef.current);
    }
  };

  const handleDoNowChange = (checked) => {
    setDoNow(checked);
    form.setFieldsValue({ do_now: checked });
    if (checked) {
      setSendReminder(false);
      form.setFieldsValue({
        send_reminder: false,
        next_follow_up_date: undefined,
        assign_to: undefined,
        remind_time: undefined,
        remind_type: undefined,
      });
    }
  };

  const resetFormForNextFollowUp = () => {
    form.resetFields();
    setDoNow(false);
    setSendReminder(false);
  };

  const handleFollowUpActivitySelect = (activityId) => {
    const catalog = activitiesCatalogRef.current.length ? activitiesCatalogRef.current : activities;
    const activity = catalog.find((a) => String(a.id) === String(activityId));
    const currentRemark = form.getFieldValue('remark');
    if (isNoAnswerFollowUpActivity(activity)) {
      form.setFieldsValue({ remark: NO_ANSWER_FOLLOW_UP_REMARK });
    } else if (currentRemark === NO_ANSWER_FOLLOW_UP_REMARK) {
      form.setFieldsValue({ remark: '' });
    }
  };

  const handleQuickEditRemark = (followUp) => {
    setEditingRemarkId(followUp.id);
    setQuickRemarkValue(followUp.remark || '');
  };

  const handleSaveQuickRemark = async (followUpId) => {
    try {
      await dealsApi.updateFollowUpRemark(followUpId, quickRemarkValue);
      message.success('توضیحات با موفقیت به‌روزرسانی شد');
      setEditingRemarkId(null);
      setQuickRemarkValue('');
      onRefresh();
    } catch (error) {
      console.error('Error updating remark:', error);
      message.error('خطا در به‌روزرسانی توضیحات');
    }
  };

  const handleCancelQuickRemark = () => {
    setEditingRemarkId(null);
    setQuickRemarkValue('');
  };

  // کلیک بیرون از کادر نباید پیش‌نویس توضیحات را دور بیندازد (مثل توضیحات عمومی معامله/سرنخ)

  const canEditFollowUp = (perms) =>
    isPermissionAllowed(perms?.edit_follow_up) || isPermissionAllowed(perms?.edit);

  const handleQuickStatusChange = async (followUpId, status) => {
    setStatusUpdatingId(followUpId);
    try {
      await dealsApi.updateFollowUpStatus(followUpId, status);
      setFollowUps((prev) =>
        prev.map((fu) => (fu.id === followUpId ? { ...fu, status } : fu)),
      );
      message.success('وضعیت پیگیری به‌روزرسانی شد');
      onRefresh();
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('خطا در به‌روزرسانی وضعیت');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const isDoNow = values.do_now === true || doNow === true;

      if (isDoNow) {
        // اگر do_now = true باشد، follow_up_activity_id، do_now، remark و deal_id ارسال می‌شود
        const payload = {
          deal_id: parseInt(dealId),
          follow_up_activity_id: values.follow_up_activity_id,
          do_now: true,
          remark: values.remark || '',
        };
        if (values.duration !== undefined && values.duration !== null && values.duration !== '') {
          payload.duration = values.duration;
        }

        if (editingFollowUp) {
          payload.id = editingFollowUp.id;
          await dealsApi.updateFollowUp(payload);
          message.success('پیگیری با موفقیت به‌روزرسانی شد');
        } else {
          await dealsApi.createFollowUp(payload);
          message.success('پیگیری با موفقیت ایجاد شد');
        }
      } else {
        let nextFollowUpDate = null;
        if (values.next_follow_up_date) {
          try {
            if (values.next_follow_up_date?.toDate && typeof values.next_follow_up_date.toDate === 'function') {
              const dateObj = values.next_follow_up_date.toDate();
              const m = moment(dateObj);
              if (m && m.isValid()) {
                nextFollowUpDate = m.format('jYYYY/jMM/jDD HH:mm');
              }
            } else if (typeof values.next_follow_up_date === 'string') {
              if (values.next_follow_up_date.match(/^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2}$/)) {
                nextFollowUpDate = values.next_follow_up_date;
              } else {
                const dateObj = moment(values.next_follow_up_date);
                if (dateObj.isValid()) {
                  nextFollowUpDate = dateObj.format('jYYYY/jMM/jDD HH:mm');
                }
              }
            }
          } catch (e) {
            console.error('Error formatting date:', e);
          }
        }

        const payload = {
          deal_id: parseInt(dealId),
          follow_up_activity_id: values.follow_up_activity_id,
          remark: values.remark || '',
        };

        if (nextFollowUpDate) {
          payload.next_follow_up_date = nextFollowUpDate;
        }
        if (values.duration !== undefined && values.duration !== null) {
          payload.duration = values.duration;
        }
        if (values.assign_to !== undefined && values.assign_to !== null) {
          payload.assign_to = values.assign_to;
        }
        if (values.send_reminder !== undefined) {
          payload.send_reminder = values.send_reminder === true || values.send_reminder === 'true' || values.send_reminder === 'yes';
          if (payload.send_reminder && values.remind_time !== undefined && values.remind_time !== null) {
            payload.remind_time = values.remind_time;
          }
          if (payload.send_reminder && values.remind_type) {
            payload.remind_type = values.remind_type;
          }
        }

        if (editingFollowUp) {
          payload.id = editingFollowUp.id;
          payload.status = normalizeFollowUpStatusForForm(
            values.status ?? editingFollowUp.status,
          );
          await dealsApi.updateFollowUp(payload);
          message.success('پیگیری با موفقیت به‌روزرسانی شد');
        } else {
          await dealsApi.createFollowUp(payload);
          message.success('پیگیری با موفقیت ایجاد شد');
        }
      }

      const addAnother =
        submitSaveModeRef.current === 'addAnother' && isDoNow && !editingFollowUp;
      submitSaveModeRef.current = 'close';

      if (addAnother) {
        resetFormForNextFollowUp();
        onRefresh();
      } else {
        handleCloseModal();
        onRefresh();
      }
    } catch (error) {
      console.error('Error saving follow-up:', error);
      if (error.status === 422) {
        show422Error(error, form);
      } else {
        message.error(editingFollowUp ? 'خطا در به‌روزرسانی پیگیری' : 'خطا در ایجاد پیگیری');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (followUpId) => {
    try {
      await dealsApi.deleteFollowUp(followUpId);
      message.success('پیگیری با موفقیت حذف شد');
      onRefresh();
    } catch (error) {
      console.error('Error deleting follow-up:', error);
      message.error('خطا در حذف پیگیری');
    }
  };

  return (
    <div className="pt-4 px-1 space-y-4">
      <div className="flex items-center justify-end mb-4">
        {canManageLeadRecord(permissions) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal(null)}
            style={{ fontFamily: 'CustomFont' }}
            className="bg-green-600 hover:bg-green-700 border-0"
          >
            افزودن پیگیری
          </Button>
        )}
      </div>

      {/* Follow-ups List */}
      {!followUps || followUps.length === 0 ? (
        <div className="py-8">
          <Empty
            description="هیچ پیگیری‌ای ثبت نشده است"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {followUps.map((followUp) => {
            const immediate = isImmediateFollowUp(followUp);
            const doNow = isDoNowFollowUp(followUp);
            const formattedDate = immediate ? null : formatDate(followUp.next_follow_up_date);
            const overdue = !immediate && isOverdue(followUp.next_follow_up_date);
            const today = !immediate && isToday(followUp.next_follow_up_date);
            const statusKey = resolveFollowUpStatus(followUp);
            const isCompleted = statusKey === 'completed';
            const completedAt = resolveFollowUpCompletedAt(followUp);
            
            const activity = resolveFollowUpActivity(
              followUp,
              activitiesCatalogRef.current.length ? activitiesCatalogRef.current : activities,
            );
            const creatorName =
              resolvePersonName(followUp.added_by) ||
              employees.find((e) => e.id === resolvePersonId(followUp.added_by))?.name;
            const assigneeName =
              resolvePersonName(followUp.assign_to) ||
              followUp.assignee?.name ||
              followUp.assigned_user?.name ||
              employees.find((e) => e.id === resolvePersonId(followUp.assign_to))?.name;
            const assigneeAvatar =
              resolvePersonAvatar(followUp.assign_to, employees) ||
              resolvePersonAvatar(followUp.assignee, employees) ||
              resolvePersonAvatar(followUp.assigned_user, employees);

            return (
              <Card
                key={followUp.id}
                className={[
                  'shadow-sm hover:shadow-md transition-all duration-200 border',
                  isCompleted
                    ? 'border-green-200 dark:border-green-800 bg-green-50/60 dark:bg-green-900/10'
                    : doNow
                      ? 'border-amber-200 dark:border-amber-800/60 bg-gradient-to-l from-amber-50/80 via-white to-white dark:from-amber-950/20 dark:via-gray-900 dark:to-gray-900'
                      : 'border-gray-200 dark:border-gray-700',
                ].join(' ')}
                style={{ fontFamily: 'CustomFont' }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Header — نوع فعالیت بالا، تاریخ زیر آن */}
                    <div className="flex flex-col items-start gap-1.5 mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isCompleted && (
                          <CheckCircleOutlined className="text-green-500 text-base flex-shrink-0" />
                        )}
                        {activity ? (
                          <ActivityTypeBadge activity={activity} />
                        ) : !isCompleted ? (
                          <CalendarOutlined
                            className={`text-base flex-shrink-0 ${
                              overdue ? 'text-red-500' : today ? 'text-orange-500' : 'text-blue-500'
                            }`}
                          />
                        ) : null}
                        {doNow && <ImmediateFollowUpBadge muted={isCompleted} />}
                      </div>
                      {formattedDate ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={[
                              isCompleted ? COMPLETED_FOLLOWUP_DATE_CLASS : 'font-semibold text-sm',
                              !isCompleted && overdue ? 'text-red-600 dark:text-red-400' : '',
                              !isCompleted && today ? 'text-orange-600 dark:text-orange-400' : '',
                              !isCompleted && !overdue && !today ? 'text-gray-800 dark:text-gray-200' : '',
                            ].filter(Boolean).join(' ')}
                            style={{ fontFamily: 'CustomFont' }}
                          >
                            {formatFollowUpDisplayLabel(followUp.next_follow_up_date) || formattedDate}
                          </span>
                          {overdue && !isCompleted && (
                            <Tooltip title="موعد گذشته">
                              <ExclamationCircleOutlined className="text-red-500 flex-shrink-0" />
                            </Tooltip>
                          )}
                          {today && !isCompleted && (
                            <Tag color="orange" style={{ fontFamily: 'CustomFont' }}>امروز</Tag>
                          )}
                        </div>
                      ) : null}
                      {completedAt ? (
                        <span
                          className="text-sm text-green-700 dark:text-green-400 font-medium inline-flex items-center gap-1"
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          <ClockCircleOutlined />
                          انجام در: {completedAt}
                        </span>
                      ) : doNow && formatFollowUpDateTime(followUp.created_at) ? (
                        <span className="text-sm text-amber-700 dark:text-amber-300 font-medium" style={{ fontFamily: 'CustomFont' }}>
                          انجام در: {formatFollowUpDateTime(followUp.created_at)}
                        </span>
                      ) : null}
                    </div>
                    
                    {/* Remark */}
                    {editingRemarkId === followUp.id ? (
                      <div className="mb-3">
                        <TextArea
                          rows={3}
                          value={quickRemarkValue}
                          onChange={(e) => setQuickRemarkValue(e.target.value)}
                          placeholder="توضیحات را وارد کنید..."
                          style={{ fontFamily: 'CustomFont', marginBottom: '8px' }}
                        />
                        <Space>
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleSaveQuickRemark(followUp.id)}
                            style={{ fontFamily: 'CustomFont' }}
                            className="bg-green-600 hover:bg-green-700 border-0"
                          >
                            ذخیره
                          </Button>
                          <Button
                            size="small"
                            onClick={handleCancelQuickRemark}
                            style={{ fontFamily: 'CustomFont' }}
                          >
                            لغو
                          </Button>
                        </Space>
                      </div>
                    ) : (
                      <div className="mb-3">
                        {followUp.remark ? (
                          <p
                            className={[
                              'text-sm leading-relaxed whitespace-pre-wrap',
                              isCompleted ? COMPLETED_FOLLOWUP_TEXT_CLASS : 'text-gray-700 dark:text-gray-300',
                            ].join(' ')}
                            style={{ fontFamily: 'CustomFont' }}
                          >
                            {followUp.remark}
                          </p>
                        ) : (
                          <p className="text-gray-400 dark:text-gray-500 text-sm italic" style={{ fontFamily: 'CustomFont' }}>
                            توضیحاتی ثبت نشده است
                          </p>
                        )}
                        {canManageLeadRecord(permissions) && (
                          <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleQuickEditRemark(followUp)}
                            className="p-0 h-auto text-blue-600 hover:text-blue-700"
                            style={{ fontFamily: 'CustomFont', fontSize: '12px' }}
                          >
                            ویرایش توضیحات
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Footer Info */}
                    <div className="flex items-center gap-4 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                      {canEditFollowUp(permissions) ? (
                        <Select
                          size="small"
                          value={statusKey}
                          onChange={(val) => handleQuickStatusChange(followUp.id, val)}
                          loading={statusUpdatingId === followUp.id}
                          disabled={statusUpdatingId != null && statusUpdatingId !== followUp.id}
                          style={{ minWidth: 110, fontFamily: 'CustomFont' }}
                          options={FOLLOW_UP_STATUS_OPTIONS}
                        />
                      ) : (
                        <Tag
                          color={getFollowUpStatusTagColor(statusKey)}
                          style={{ fontFamily: 'CustomFont', fontSize: '11px' }}
                        >
                          {getFollowUpStatusLabel(statusKey)}
                        </Tag>
                      )}
                      {followUp.duration && (
                        <span style={{ fontFamily: 'CustomFont' }}>
                          <ClockCircleOutlined className="mr-1" />
                          مدت زمان: {followUp.duration} دقیقه
                        </span>
                      )}
                      {/* ایجادکننده */}
                      {creatorName && (
                        <Tooltip title="ایجادکننده">
                          <span style={{ fontFamily: 'CustomFont' }} className="flex items-center gap-1">
                            <UserOutlined className="text-green-500" />
                            <span>ایجاد: {creatorName}</span>
                          </span>
                        </Tooltip>
                      )}
                      {formatFollowUpDateTime(followUp.created_at) && (
                        <Tooltip title="تاریخ ثبت فعالیت">
                          <span style={{ fontFamily: 'CustomFont' }} className="flex items-center gap-1">
                            <CalendarOutlined className="text-gray-400" />
                            <span>ثبت: {formatFollowUpDateTime(followUp.created_at)}</span>
                          </span>
                        </Tooltip>
                      )}
                      {isFollowUpEdited(followUp) && formatFollowUpDateTime(followUp.updated_at) && (
                        <Tooltip title="تاریخ ویرایش">
                          <span style={{ fontFamily: 'CustomFont' }} className="flex items-center gap-1">
                            <EditOutlined className="text-amber-500" />
                            <span>ویرایش: {formatFollowUpDateTime(followUp.updated_at)}</span>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="مسئول انجام این فعالیت">
                        <span style={{ fontFamily: 'CustomFont' }} className="flex items-center gap-1.5">
                          <Avatar
                            size={22}
                            src={resolveAvatarSrc(assigneeAvatar)}
                            icon={<UserOutlined />}
                            className="flex-shrink-0 border border-gray-200 dark:border-gray-600"
                          />
                          <span>
                            مسئول انجام فعالیت: {assigneeName || 'تعریف نشده'}
                          </span>
                        </span>
                      </Tooltip>
                      {(followUp.send_reminder === true || followUp.send_reminder === 'true' || followUp.send_reminder === 1 || followUp.send_reminder === 'yes') && (
                        <span style={{ fontFamily: 'CustomFont' }}>
                          <BellOutlined className="mr-1" />
                          یادآوری: {followUp.remind_time} {followUp.remind_type === 'minute' ? 'دقیقه' : followUp.remind_type === 'hour' ? 'ساعت' : 'روز'} قبل
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <Space className="flex-shrink-0">
                    {canManageLeadRecord(permissions) && (
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        style={{ fontFamily: 'CustomFont' }}
                        title="ویرایش"
                        onClick={() => handleOpenModal(followUp)}
                        loading={editLoading && editingFollowUp?.id === followUp.id}
                      />
                    )}
                    {canDeleteLeadRecord(permissions) && (
                      <Popconfirm
                        title="حذف پیگیری"
                        description="آیا از حذف این پیگیری اطمینان دارید؟"
                        onConfirm={() => handleDelete(followUp.id)}
                        okText="بله، حذف کن"
                        cancelText="لغو"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          className="hover:bg-red-50 dark:hover:bg-red-900/20"
                          style={{ fontFamily: 'CustomFont' }}
                          title="حذف"
                        />
                      </Popconfirm>
                    )}
                  </Space>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            {editingFollowUp ? 'ویرایش پیگیری' : 'افزودن پیگیری جدید'}
          </span>
        }
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={700}
        style={{ fontFamily: 'CustomFont' }}
        maskClosable={false}
        keyboard={false}
        destroyOnClose
      >
        {(editLoading && editingFollowUp) || modalDataLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
            <div className="text-gray-500" style={{ fontFamily: 'CustomFont' }}>
              در حال بارگذاری اطلاعات...
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ fontFamily: 'CustomFont' }}
            className="mt-4"
          >
            {/* Do Now Switch */}
            <Form.Item
              name="do_now"
              label={<span style={{ fontFamily: 'CustomFont' }}>این پیگیری انجام شده</span>}
              valuePropName="checked"
            >
              <Switch
                checkedChildren="بله"
                unCheckedChildren="خیر"
                onChange={handleDoNowChange}
              />
            </Form.Item>

            {/* Activity - همیشه نمایش داده می‌شود */}
            <Form.Item
              name="follow_up_activity_id"
              label={<span style={{ fontFamily: 'CustomFont' }}>نوع فعالیت</span>}
              rules={[{ required: true, message: 'لطفاً نوع فعالیت را انتخاب کنید' }]}
            >
              <FollowUpActivityPicker
                activities={activitiesCatalogRef.current.length ? activitiesCatalogRef.current : activities}
                loading={activitiesLoading}
                onAfterChange={handleFollowUpActivitySelect}
              />
            </Form.Item>

            {/* Next Follow-up Date & Time - فقط اگر do_now = false */}
            {!doNow && (
              <Form.Item
                name="next_follow_up_date"
                label={<span style={{ fontFamily: 'CustomFont' }}>تاریخ و زمان پیگیری بعدی</span>}
                rules={[{ required: true, message: 'لطفاً تاریخ و زمان پیگیری بعدی را انتخاب کنید' }]}
              >
                <FollowUpSchedulePicker
                  placeholder="انتخاب تاریخ و زمان"
                  showTime={true}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            )}

            {/* Duration */}
            <Form.Item
              name="duration"
              label={<span style={{ fontFamily: 'CustomFont' }}>مدت زمان (دقیقه)</span>}
            >
              <InputNumber
                min={1}
                placeholder="مدت زمان به دقیقه"
                style={{ width: '100%', fontFamily: 'CustomFont' }}
                size="large"
              />
            </Form.Item>

            {/* Assign To - فقط اگر do_now = false */}
            {!doNow && (
              <Form.Item
                name="assign_to"
                label={<span style={{ fontFamily: 'CustomFont' }}>واگذاری به</span>}
              >
                <Select
                  placeholder="انتخاب کارمند"
                  loading={employeesLoading}
                  showSearch
                  filterOption={(input, option) => {
                    const label = option?.label?.props?.children?.[1] || option?.children || '';
                    return String(label).toLowerCase().includes(input.toLowerCase());
                  }}
                  style={{ fontFamily: 'CustomFont' }}
                  size="large"
                >
                  {employees.map((emp) => (
                    <Option key={emp.id} value={emp.id} label={emp.name}>
                      <div className="flex items-center gap-2">
                        {resolveAvatarSrc(emp.image_url) ? (
                          <img src={resolveAvatarSrc(emp.image_url)} alt={emp.name} className="w-6 h-6 rounded-full" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                            <UserOutlined />
                          </div>
                        )}
                        <span>{emp.name}</span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {/* Send Reminder - فقط اگر do_now = false */}
            {!doNow && (
              <>
                <Form.Item
                  name="send_reminder"
                  label={<span style={{ fontFamily: 'CustomFont' }}>ارسال یادآوری</span>}
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="فعال"
                    unCheckedChildren="غیرفعال"
                    onChange={(checked) => setSendReminder(checked)}
                  />
                </Form.Item>

                {sendReminder && (
                  <div className="grid grid-cols-2 gap-4">
                    <Form.Item
                      name="remind_time"
                      label={
                        <span className="inline-flex items-center gap-1.5" style={{ fontFamily: 'CustomFont' }}>
                          زمان یادآوری
                          <Tooltip title="چند واحد (دقیقه، ساعت یا روز) قبل از زمان پیگیری، یادآوری برای مسئول ارسال شود.">
                            <QuestionCircleOutlined className="text-gray-400 cursor-help text-xs" />
                          </Tooltip>
                        </span>
                      }
                      rules={[{ required: true, message: 'لطفاً زمان یادآوری را وارد کنید' }]}
                    >
                      <InputNumber
                        min={1}
                        placeholder="مثال: 2"
                        style={{ width: '100%', fontFamily: 'CustomFont' }}
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="remind_type"
                      label={<span style={{ fontFamily: 'CustomFont' }}>نوع یادآوری</span>}
                      rules={[{ required: true, message: 'لطفاً نوع یادآوری را انتخاب کنید' }]}
                    >
                      <Select
                        placeholder="انتخاب نوع"
                        style={{ fontFamily: 'CustomFont' }}
                        size="large"
                      >
                        <Option value="minute">دقیقه</Option>
                        <Option value="hour">ساعت</Option>
                        <Option value="day">روز</Option>
                      </Select>
                    </Form.Item>
                  </div>
                )}
              </>
            )}

            {/* Status (only for editing and when do_now = false) */}
            {editingFollowUp && !doNow && (
              <Form.Item
                name="status"
                label={<span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>}
              >
                <Select
                  style={{ fontFamily: 'CustomFont' }}
                  size="large"
                >
                  <Option value="pending">
                    <Tag color="orange">در انتظار</Tag>
                  </Option>
                  <Option value="completed">
                    <Tag color="green">انجام شده</Tag>
                  </Option>
                  <Option value="canceled">
                    <Tag color="default">لغو شده</Tag>
                  </Option>
                </Select>
              </Form.Item>
            )}

            {/* Remark */}
            <Form.Item
              name="remark"
              label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}
            >
              <TextArea
                rows={4}
                placeholder="توضیحات پیگیری را وارد کنید..."
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 mt-6">
              <Button
                onClick={handleCloseModal}
                disabled={loading}
                style={{ fontFamily: 'CustomFont' }}
              >
                لغو
              </Button>
              {doNow && !editingFollowUp ? (
                <>
                  <Button
                    type="primary"
                    loading={loading}
                    onClick={() => {
                      submitSaveModeRef.current = 'close';
                      form.submit();
                    }}
                    style={{ fontFamily: 'CustomFont' }}
                    className="bg-green-600 hover:bg-green-700 border-0"
                  >
                    ذخیره
                  </Button>
                  <Button
                    type="primary"
                    loading={loading}
                    onClick={() => {
                      submitSaveModeRef.current = 'addAnother';
                      form.submit();
                    }}
                    style={{ fontFamily: 'CustomFont' }}
                    className="bg-emerald-600 hover:bg-emerald-700 border-0"
                  >
                    ذخیره و افزودن پیگیری بعدی
                  </Button>
                </>
              ) : (
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  style={{ fontFamily: 'CustomFont' }}
                  className="bg-green-600 hover:bg-green-700 border-0"
                >
                  {editingFollowUp ? 'ذخیره تغییرات' : 'ایجاد پیگیری'}
                </Button>
              )}
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default FollowUpTab;
